import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { BtnGhost, BtnPrimary, Field, TextInput } from '@/components/field'
import { EmptyState } from '@/components/empty-state'
import { Modal } from '@/components/modal'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'
import { fmtDate } from '@/lib/labels'

type Promo = {
  id: string
  code: string
  percent: number
  minSubtotalCents: number | null
  firstOrderOnly: boolean
  maxRedemptions: number | null
  redemptionCount?: number
  startsAt: string | null
  endsAt: string | null
  active: boolean
}

type Form = {
  code: string
  percent: string
  minSubtotalDollars: string
  firstOrderOnly: boolean
  maxRedemptions: string
  active: boolean
}

const emptyForm = (): Form => ({
  code: '',
  percent: '10',
  minSubtotalDollars: '',
  firstOrderOnly: false,
  maxRedemptions: '',
  active: true,
})

export function CodesPromo() {
  const toast = useToast()
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Promo | null>(null)
  const [form, setForm] = useState<Form>(emptyForm())
  const [deactivate, setDeactivate] = useState<Promo | null>(null)

  const list = useQuery({
    queryKey: ['admin-promos'],
    queryFn: () => api<{ items: Promo[] }>('/admin/promo-codes'),
  })

  const save = useMutation({
    mutationFn: () => {
      const percent = Number.parseInt(form.percent, 10)
      if (!Number.isFinite(percent) || percent < 1 || percent > 100) {
        throw new Error('Le pourcentage doit être entre 1 et 100.')
      }
      const body = {
        percent,
        minSubtotalCents: form.minSubtotalDollars
          ? Math.round(Number.parseFloat(form.minSubtotalDollars) * 100)
          : undefined,
        firstOrderOnly: form.firstOrderOnly,
        maxRedemptions: form.maxRedemptions
          ? Number.parseInt(form.maxRedemptions, 10)
          : undefined,
        active: form.active,
      }
      if (editing) {
        return apiMutate(`/admin/promo-codes/${editing.id}`, 'PATCH', body)
      }
      return apiMutate('/admin/promo-codes', 'POST', {
        ...body,
        code: form.code,
      })
    },
    onSuccess: () => {
      toast.ok(editing ? 'Code mis à jour' : 'Code créé')
      setModalOpen(false)
      setEditing(null)
      void qc.invalidateQueries({ queryKey: ['admin-promos'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const remove = useMutation({
    mutationFn: () => apiMutate(`/admin/promo-codes/${deactivate!.id}`, 'DELETE'),
    onSuccess: () => {
      toast.ok('Code désactivé')
      setDeactivate(null)
      void qc.invalidateQueries({ queryKey: ['admin-promos'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  function openCreate() {
    setEditing(null)
    setForm(emptyForm())
    setModalOpen(true)
  }

  function openEdit(p: Promo) {
    setEditing(p)
    setForm({
      code: p.code,
      percent: String(p.percent),
      minSubtotalDollars: p.minSubtotalCents != null ? (p.minSubtotalCents / 100).toFixed(2) : '',
      firstOrderOnly: p.firstOrderOnly,
      maxRedemptions: p.maxRedemptions != null ? String(p.maxRedemptions) : '',
      active: p.active,
    })
    setModalOpen(true)
  }

  const items = list.data?.items ?? []

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-end">
        <BtnPrimary onClick={openCreate}>Nouveau code</BtnPrimary>
      </div>

      {list.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState
          title="Aucun code promo"
          body="Créez un code pour offrir une remise."
          action={<BtnPrimary onClick={openCreate}>Nouveau code</BtnPrimary>}
        />
      ) : (
        <section className="carte-admin overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['Code', '%', 'Min.', '1ʳᵉ cmd', 'Actif', 'Fin', ''].map((h) => (
                  <th key={h || 'a'} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-bordure-admin last:border-0">
                  <td className="px-4 py-2.5 font-medium">{p.code}</td>
                  <td className="px-4 py-2.5 tabular-nums">{p.percent}%</td>
                  <td className="px-4 py-2.5 tabular-nums">
                    {p.minSubtotalCents != null ? `$${(p.minSubtotalCents / 100).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-2.5">{p.firstOrderOnly ? 'Oui' : 'Non'}</td>
                  <td className="px-4 py-2.5">
                    <StatusPill
                      label={p.active ? 'Actif' : 'Inactif'}
                      tone={p.active ? 'ok' : 'neutral'}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-second-admin">{fmtDate(p.endsAt)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <RowActions
                      items={[
                        { label: 'Modifier', onSelect: () => openEdit(p) },
                        ...(p.active
                          ? [
                              {
                                label: 'Désactiver',
                                danger: true,
                                onSelect: () => setDeactivate(p),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <Modal
        open={modalOpen}
        title={editing ? `Modifier ${editing.code}` : 'Nouveau code promo'}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <BtnGhost onClick={() => setModalOpen(false)}>Annuler</BtnGhost>
            <BtnPrimary disabled={save.isPending} onClick={() => save.mutate()}>
              Enregistrer
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {!editing ? (
            <Field label="Code">
              <TextInput
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              />
            </Field>
          ) : null}
          <Field label="Pourcentage">
            <TextInput
              type="number"
              min="1"
              max="100"
              value={form.percent}
              onChange={(e) => setForm((f) => ({ ...f, percent: e.target.value }))}
            />
          </Field>
          <Field label="Sous-total mini (USD)">
            <TextInput
              type="number"
              step="0.01"
              value={form.minSubtotalDollars}
              onChange={(e) => setForm((f) => ({ ...f, minSubtotalDollars: e.target.value }))}
            />
          </Field>
          <Field label="Max utilisations">
            <TextInput
              type="number"
              value={form.maxRedemptions}
              onChange={(e) => setForm((f) => ({ ...f, maxRedemptions: e.target.value }))}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.firstOrderOnly}
              onChange={(e) => setForm((f) => ({ ...f, firstOrderOnly: e.target.checked }))}
            />
            Première commande uniquement
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
            />
            Actif
          </label>
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(deactivate)}
        title="Désactiver ce code ?"
        body={`Le code ${deactivate?.code ?? ''} ne pourra plus être utilisé.`}
        confirmLabel="Désactiver"
        danger
        busy={remove.isPending}
        onClose={() => setDeactivate(null)}
        onConfirm={() => remove.mutate()}
      />
    </div>
  )
}
